import { getApiBaseUrl, Hair_API_PREFIX } from './apiConfig';
import { resolveHairTenantPortal } from '../config/hairApp';
import { fetchWithTimeout } from './fetchWithTimeout';

const INSIGHT = `${Hair_API_PREFIX}/insight`;

export const QUICK_WIDE_VIEW = 'overview';
export const DETAILED_WIDE_VIEWS = ['front', 'left', 'right', 'top', 'back'];
export const WIDE_VIEWS = [QUICK_WIDE_VIEW, ...DETAILED_WIDE_VIEWS];
export const MICRO_VIEWS = [
  'hairline',
  'crown',
  'top',
  'scalp_detail',
  'left_temple',
  'right_temple',
  'other',
];
export const MICRO_OVERRIDE_CHIPS = [
  { id: 'hairline', label: 'Hairline' },
  { id: 'crown', label: 'Crown' },
  { id: 'top', label: 'Top' },
  { id: 'left_temple', label: 'Left' },
  { id: 'right_temple', label: 'Right' },
  { id: 'other', label: 'Other' },
];

/** UI-only guidance regions (normalized). Not anatomical measurements. */
export const HAIR_REGIONS = {
  hairline: { x: 0.5, y: 0.18, width: 0.6, height: 0.18 },
  top: { x: 0.5, y: 0.38, width: 0.55, height: 0.25 },
  crown: { x: 0.5, y: 0.62, width: 0.4, height: 0.25 },
  left_temple: { x: 0.22, y: 0.28, width: 0.22, height: 0.2 },
  right_temple: { x: 0.78, y: 0.28, width: 0.22, height: 0.2 },
  scalp_detail: { x: 0.5, y: 0.48, width: 0.35, height: 0.28 },
  other: { x: 0.5, y: 0.5, width: 0.4, height: 0.3 },
};

function base() {
  return (getApiBaseUrl() || '').replace(/\/$/, '');
}

const RETRY_STATUS = new Set([502, 503, 504]);

async function insightFetch(path, options = {}) {
  const url = `${base()}${INSIGHT}${path}`;
  const timeoutMs = options.timeoutMs || 30000;
  const maxAttempts = options.retry === false ? 1 : 3;
  let response;
  let lastErr;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      response = await fetchWithTimeout(url, {
        timeoutMs,
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {}),
        },
        ...options,
      });
      if (RETRY_STATUS.has(response.status) && attempt < maxAttempts - 1) {
        await new Promise((r) => window.setTimeout(r, 800 + attempt * 900));
        continue;
      }
      break;
    } catch (err) {
      lastErr = err;
      const retryable =
        err?.name === 'TimeoutError' ||
        /failed to fetch|network|load failed/i.test(String(err?.message || ''));
      if (retryable && attempt < maxAttempts - 1) {
        await new Promise((r) => window.setTimeout(r, 800 + attempt * 900));
        continue;
      }
      if (err?.name === 'TimeoutError') throw err;
      throw new Error('Analysis is temporarily unavailable. Please try again.');
    }
  }

  if (!response) {
    if (lastErr?.name === 'TimeoutError') throw lastErr;
    throw new Error('Analysis is temporarily unavailable. Please try again.');
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = data.error || data.message || data.detail;
    if (detail) throw new Error(detail);
    if (response.status === 413) {
      throw new Error('Image is too large. Try a smaller photo or capture from the camera.');
    }
    if (response.status >= 500) {
      throw new Error('Analysis is temporarily unavailable. Please try again.');
    }
    throw new Error(`Insight request failed (${response.status})`);
  }
  return data;
}

export async function createInsightSession(customerId, opts = {}) {
  const salonReference = opts.salonReference || opts.salon_reference || opts.sessionId || undefined;
  return insightFetch('/session', {
    method: 'POST',
    body: JSON.stringify({
      customerId: customerId || 'GUEST',
      customerName: opts.customerName || opts.customer_name || customerId,
      scanMode: opts.scanMode || 'advanced',
      salonReference,
      tenantPortal: opts.tenantPortal || opts.tenant_portal || resolveHairTenantPortal(),
      organizationId: opts.organizationId ?? opts.organization_id,
      franchiseId: opts.franchiseId ?? opts.franchise_id,
      checkinId: opts.checkinId ?? opts.checkin_id,
    }),
  });
}

export async function getInsightSession(sessionId) {
  return insightFetch(`/session/${encodeURIComponent(sessionId)}`);
}

export async function getInsightGuidance() {
  return insightFetch('/guidance');
}

export async function getInsightStatus() {
  return insightFetch('/status');
}

export async function uploadInsightCapture(sessionId, payload) {
  return insightFetch(`/session/${encodeURIComponent(sessionId)}/capture`, {
    method: 'POST',
    body: JSON.stringify(payload),
    timeoutMs: 60000,
  });
}

export async function setInsightTarget(sessionId, target) {
  return insightFetch(`/session/${encodeURIComponent(sessionId)}/target`, {
    method: 'POST',
    body: JSON.stringify({ target }),
  });
}

export async function analyzeInsightWide(sessionId) {
  return insightFetch(`/session/${encodeURIComponent(sessionId)}/analyze-wide`, {
    method: 'POST',
    body: JSON.stringify({}),
    timeoutMs: 180000,
  });
}

/** @deprecated alias — prefer analyzeInsight */
export async function runInsightAnalysis(sessionId, opts = {}) {
  return analyzeInsight(sessionId, opts);
}

export async function analyzeInsight(sessionId, opts = {}) {
  return insightFetch(`/session/${encodeURIComponent(sessionId)}/analyze`, {
    method: 'POST',
    body: JSON.stringify({
      partialAnalysis: Boolean(opts.partialAnalysis),
    }),
    timeoutMs: 300000,
  });
}

export async function getInsightDashboard(sessionId) {
  return insightFetch(`/session/${encodeURIComponent(sessionId)}/dashboard`, {
    timeoutMs: 60000,
  });
}

function insightReportPdfBody(dashboard, sessionId) {
  const sid = sessionId || dashboard?.sessionId || null;
  const body = {
    sessionId: sid,
    orgName: dashboard?.orgName || null,
    orgLogoUrl: dashboard?.orgLogoUrl || null,
    recommendedTreatments: dashboard?.recommendedTreatments || null,
  };
  // Full dashboard only when session file may be stale (in-flight analysis).
  if (dashboard?.visualStory || dashboard?.captures || dashboard?.coverageMap) {
    body.dashboard = dashboard;
  }
  return body;
}

export async function downloadInsightReportPdf(dashboard, sessionId) {
  const url = `${base()}${INSIGHT}/report-pdf`;
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(insightReportPdfBody(dashboard, sessionId)),
    timeoutMs: 60000,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to generate insight PDF report');
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  const sid = (sessionId || dashboard?.sessionId || 'insight').toString().replace(/[^\w-]+/g, '_');
  a.download = `Hair_Insight_Report_${sid}_${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Durable PDF from tenant salon DB (Enterprise or Naturals). */
export function buildSalonStoredHairInsightPdfUrl(sessionId) {
  const sid = String(sessionId || '').trim();
  if (!sid.startsWith('HA-')) return null;
  const origin =
    (typeof window !== 'undefined' && window.location?.origin) || '';
  return `${origin}/api/salon/hair-insight/sessions/${encodeURIComponent(sid)}/report-pdf`;
}

export async function createInsightReportPdfDownloadUrl(dashboard, sessionId) {
  const stored = buildSalonStoredHairInsightPdfUrl(sessionId || dashboard?.sessionId);
  if (stored) {
    try {
      const probe = await fetch(stored, { method: 'HEAD' });
      if (probe.ok) return stored;
    } catch {
      /* fall back to hair-analyzer QR flow */
    }
  }
  const url = `${base()}${INSIGHT}/report-pdf-qr`;
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(insightReportPdfBody(dashboard, sessionId)),
    timeoutMs: 60000,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to create insight PDF download link');
  }
  return resolveInsightPdfDownloadUrl(data);
}

function resolveInsightPdfDownloadUrl(data) {
  const token = typeof data?.token === 'string' ? data.token.trim() : '';
  let apiBase = base();
  if (!apiBase && typeof window !== 'undefined' && window.location?.origin) {
    apiBase = window.location.origin;
  }
  if (token) {
    return `${apiBase}${INSIGHT}/report-pdf/${encodeURIComponent(token)}`;
  }
  if (!data?.download_url) throw new Error('PDF download link missing');
  return data.download_url;
}

export async function createInsightReportPdfDownloadUrlFromCapture({
  rootEl,
  sessionId,
  timeoutMs = 120000,
} = {}) {
  const { captureReportPageImages } = await import('../../../../enterprise/shared/captureReportPageImages');
  if (!rootEl) throw new Error('Report layout not ready');
  const pages = await captureReportPageImages(rootEl, {
    scale: 1.75,
    jpegQuality: 0.88,
    maxPages: 16,
  });
  const url = `${base()}${INSIGHT}/report-pdf-from-capture`;
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: sessionId || null,
      pages,
    }),
    timeoutMs,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to create insight PDF download link');
  }
  return resolveInsightPdfDownloadUrl(data);
}

export async function compareInsightSessions(previousSessionId, currentSessionId) {
  return insightFetch('/compare', {
    method: 'POST',
    body: JSON.stringify({ previousSessionId, currentSessionId }),
  });
}

export function resolveInsightAssetUrl(url) {
  if (!url) return url;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  const b = base();
  return b ? `${b}${url.startsWith('/') ? url : `/${url}`}` : url;
}

export function targetDisplayName(target) {
  const map = {
    hairline: 'Hairline',
    crown: 'Crown',
    top: 'Top',
    scalp_detail: 'Standard scalp area',
    left_temple: 'Left temple',
    right_temple: 'Right temple',
    left: 'Left',
    right: 'Right',
    other: 'Targeted area',
  };
  return map[target] || (target || 'Scalp').replace(/_/g, ' ');
}
